import { ProductVariationModel } from '@simploshop-models/product-variation.model'

export default defineNuxtPlugin({
    name: 'model-customization',
    parallel: true,
    setup: () => {
        const runtimeConfig = useRuntimeConfig()


        const customerCreditsAccountId = Number(runtimeConfig.public.id.customerCreditsAccount)
        // CartModel
        CartModel.prototype.getRemainingCustomerCredits = function () {
            return this.customerAccountAmountsOfItems?.find(acc => acc.customer_account_id === customerCreditsAccountId)
                ?.amounts_remain ?? 0
        }

        CartModel.prototype.getCurrentCustomerCreditsAmount = function () {
            return this.customerAccountSettings?.find(acc => acc.customer_account_id === customerCreditsAccountId)
                ?.amount ?? 0
        }

        CartModel.prototype.getMinOrderPriceForCreditsToBeAllowed = function () {
            return this.customerAccountSettings?.find(acc => acc.customer_account_id === customerCreditsAccountId)
                ?.min_order_price ?? null
        }

        CartModel.prototype.isAllowedToApplyCredits = function () {
            const minOrderPrice = this.getMinOrderPriceForCreditsToBeAllowed()
            if (minOrderPrice === null) return true

            const currentCartPrice = this.getTotalPrice()
            if (currentCartPrice === null) return false

            return getPriceValue(currentCartPrice) >= getPriceValue(minOrderPrice)
        }

        CartModel.prototype.getMaxApplicableCreditsForOrder = function () {
            return this.customerAccountSettings?.find(acc => acc.customer_account_id === customerCreditsAccountId)
                ?.max_usable_amount ?? 0
        }

        CartModel.prototype.canApplyCredits = function (amount: number) {
            if (amount < 0) return false

            const maxUsable = this.getMaxApplicableCreditsForOrder()
            if (amount > maxUsable) return false

            const remaining = this.getRemainingCustomerCredits()
            if (amount > remaining) return false


            const currentCartPrice = this.getTotalPrice()
            const minOrderPrice = this.getMinOrderPriceForCreditsToBeAllowed()

            if (currentCartPrice === null || minOrderPrice === null) return false
            if (getPriceValue(currentCartPrice) < getPriceValue(minOrderPrice)) return false

            return true
        }

        CartModel.prototype.getAppliedCredits = function () {
            return this.customerAccounts?.find(acc => acc.customer_account_id === customerCreditsAccountId)
                ?.amount ?? null
        }

        CartModel.prototype.getCustomerCreditsToBeGained = function () {
            return this.customerAccountAmountsOfOrder?.find(acc => acc.customer_account_id === customerCreditsAccountId)
                ?.amounts ?? 0
        }

        CartModel.prototype.getRemainingCustomerCreditsValuation = function () {
            return this.customerAccountAmountsOfItems?.find(acc => acc.customer_account_id === customerCreditsAccountId)
                ?.converted_amounts_remain ?? null
        }

        CartModel.prototype.getCustomerCreditsToBeGainedValuation = function () {
            return this.customerAccountAmountsOfOrder?.find(acc => acc.customer_account_id === customerCreditsAccountId)
                ?.converted_amounts ?? null
        }


        // CustomerModel
        CustomerModel.prototype.getTotalCredits = function () {
            return this.customerAccountAmounts?.find(acc => acc.customerAccountId === customerCreditsAccountId)
                ?.amount ?? 0
        }

        CustomerModel.prototype.getTotalCreditsValuation = function () {
            return this.customerAccountAmounts?.find(acc => acc.customerAccountId === customerCreditsAccountId)
                ?.conversionValueWholeAccount ?? null
        }

        CustomerModel.prototype.getTotalCreditsReceived = function () {
            return this.customerAccountAmounts?.find(acc => acc.customerAccountId === customerCreditsAccountId)
                ?.receivedAmount ?? 0
        }

        CustomerModel.prototype.getTotalCreditsUsed = function () {
            return this.customerAccountAmounts?.find(acc => acc.customerAccountId === customerCreditsAccountId)
                ?.usedAmount ?? 0
        }

        CustomerModel.prototype.getTotalCreditsReceivedValuation = function () {
            return this.customerAccountAmounts?.find(acc => acc.customerAccountId === customerCreditsAccountId)
                ?.conversionValueReceivedAmount ?? null
        }

        CustomerModel.prototype.getTotalCreditsUsedValuation = function () {
            return this.customerAccountAmounts?.find(acc => acc.customerAccountId === customerCreditsAccountId)
                ?.conversionValueUsedAmount ?? null
        }

        // ProductModel
        ProductModel.prototype.canBePurchasedForCredits = function (variation?: ProductVariationModel | null) {
            if (!this.customerAccountProducts) return false

            if (!variation) {
                return this.customerAccountProducts?.some(product => product.can_use_credits && product.customer_account_id === customerCreditsAccountId) ?? false
            }

            return variation.customerAccountProducts?.find(entry => entry.customer_account_id === customerCreditsAccountId)?.can_use_credits ?? false
        }

        ProductModel.prototype.getAmountOfCreditsForOrder = function (variation?: ProductVariationModel | null) {
            const customerAccountProducts = variation ? variation.customerAccountProducts : this.customerAccountProducts

            if (!customerAccountProducts) return null

            return customerAccountProducts?.find(product => (!('product_variation_id' in product) || product.product_variation_id === (variation?.id ?? null)) && product.customer_account_id === customerCreditsAccountId)?.amount_from_order ?? null
        }

        // OrderItemModel
        OrderItemModel.prototype.getAmountOfCreditsUsed = function () {
            return this.customerAccount?.amount ?? null
        }
    },
})
